import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { WorkflowsActions } from '../actions/workflows.actions';
import { WorkflowDto } from '../dtos/workflow-dto.interface';
import { SubjectType } from '../enums/subject-type.enum';
import { WorkflowArea } from '../enums/workflow-area.enum';
import { WorkflowsService } from '../services/workflows.service';

@Injectable()
export class WorkflowsEffects {
    constructor(private actions$: Actions, private workflowsService: WorkflowsService) {}

    loadWorkflows$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.loadWorkflows),
            mergeMap((action) => {
                return this.workflowsService.getWorkflows(action.filter).pipe(
                    map((response: WorkflowDto[]) => WorkflowsActions.workflowsLoaded({ response })),
                    catchError((error) => of(WorkflowsActions.workflowsLoadFailure({ error }))),
                );
            }),
        ),
    );

    loadPersonMandateManagementWorkflow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.loadPersonMandateManagementWorkflow),
            mergeMap(() =>
                this.workflowsService
                    .getWorkflows({ area: WorkflowArea.MANDATE_MANAGEMENT, subjectType: SubjectType.PERSON })
                    .pipe(
                        map((response: WorkflowDto[]) =>
                            WorkflowsActions.personMandateManagementWorkflowLoaded({ response }),
                        ),
                        catchError((error) => of(WorkflowsActions.personMandateManagementWorkflowLoadFailure({ error }))),
                    ),
            ),
        ),
    );

    loadWorkflowById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.loadWorkflowById),
            mergeMap((action) => {
                return this.workflowsService.getWorkflowById(action.workflowId).pipe(
                    map((response: WorkflowDto) => WorkflowsActions.workflowByIdLoaded({ response })),
                    catchError((error) => of(WorkflowsActions.workflowByIdLoadFailure({ error }))),
                );
            }),
        ),
    );

    createWorkflow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.createWorkflow),
            mergeMap((action) =>
                this.workflowsService.createWorkflow(action.createWorkflowDto).pipe(
                    map((response) => WorkflowsActions.workflowCreated({ response })),
                    catchError((error) => of(WorkflowsActions.workflowCreateFailure({ error }))),
                ),
            ),
        ),
    );

    updateWorkflow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.updateWorkflow),
            mergeMap((action) =>
                this.workflowsService.updateWorkflow(action.workflowId, action.updateWorkflowDto).pipe(
                    map((response) => WorkflowsActions.workflowUpdated({ response })),
                    catchError((error) => of(WorkflowsActions.workflowUpdateFailure({ error }))),
                ),
            ),
        ),
    );

    deleteWorkflow$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.deleteWorkflow),
            mergeMap((action) =>
                this.workflowsService.deleteWorkflow(action.workflowId).pipe(
                    map(() => WorkflowsActions.workflowDeleted({ workflowId: action.workflowId })),
                    catchError((error) => of(WorkflowsActions.workflowDeleteFailure({ error }))),
                ),
            ),
        ),
    );
}
